// src/pages/Home.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { dealAPI, categoryAPI, cityAPI, brandAPI } from "../services/api";
import DealCard from "../components/deals/DealCard";
import Loading from "../components/common/Loading";
import AdBanner from "../components/common/AdBanner";
import { useSettings } from "../context/SettingsContext";
import FlowerDecor from "../components/common/FlowerDecor";
import useSeo from "../hooks/useSeo";
import QuickTiles from "../components/home/QuickTiles";
import MobileDiscover from "../components/home/MobileDiscover";

import { FiMapPin, FiChevronLeft, FiChevronRight } from "react-icons/fi";

import normalizeImagePath from "../utils/imageUtils";
import { renderCategoryIcon } from "../utils/categoryIcons";
import { localizedName } from "../utils/localizedName";
import { useLanguage } from "../i18n/LanguageContext";

const HERO_BG = "/image.jpg";

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};
    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);
    if (payload.data && (payload.data.total ?? payload.data.totalItems)) return Number(payload.data.total ?? payload.data.totalItems);
    const headerTotal = res?.headers?.["x-total-count"] || res?.headers?.["X-Total-Count"];
    if (headerTotal) return Number(headerTotal);
  } catch {}
  return 0;
};

const normalizeDealsImages = (list) =>
  (Array.isArray(list) ? list : []).map((d) => {
    try {
      const copy = { ...d };
      // images peut arriver en tableau OU en chaîne JSON / CSV
      // (colonnes JSON relues en string après migration MySQL).
      // On ne l'écrase JAMAIS : on parse, sinon on laisse tel quel pour
      // que DealCard le résolve (comme la page détail).
      let imgs = copy.images;
      if (typeof imgs === "string" && imgs.trim()) {
        const raw = imgs.trim();
        try {
          const parsed = JSON.parse(raw);
          imgs = Array.isArray(parsed) ? parsed : raw.split(",").map((s) => s.trim()).filter(Boolean);
        } catch {
          imgs = raw.split(",").map((s) => s.trim()).filter(Boolean);
        }
      }
      if (Array.isArray(imgs) && imgs.length > 0) {
        copy.images = imgs
          .map((img) => {
            try {
              return normalizeImagePath(img) || img;
            } catch {
              return img;
            }
          })
          .filter(Boolean);
      } else {
        copy.images = Array.isArray(imgs) ? imgs : [];
      }
      return copy;
    } catch {
      return d;
    }
  });

// v1.418.0 — getDiscountPercent() a ete retire d'ici : il ne servait qu'a
// classer les « offres en vedette » par remise decroissante, ce qui figeait la
// selection. L'ordre vient maintenant du serveur (rotation par creneau).
// Laisser la fonction en place aurait casse le build : react-scripts compile
// avec CI=true et transforme l'avertissement « defini mais jamais utilise » en
// erreur bloquante. DealCard calcule sa propre remise, rien d'autre ne l'appelait.

const pickCityFromDeal = (deal) => {
  // MySQL : le backend renvoie `city` (nom) + `citySlug` + `cityRef`
  const fromCity = deal?.city || deal?.cityName || deal?.cityRef?.name || deal?.citySlug || "";
  // Legacy Mongo : location / locations
  const fromLocations = Array.isArray(deal?.locations) && deal.locations.length ? deal.locations[0] : "";
  const fromLocation = deal?.location || "";

  const raw = String(fromCity || fromLocations || fromLocation || "").trim();
  if (!raw) return "";

  const first = raw.split(",")[0].trim();
  return first.replace(/\s+/g, " ");
};

const Home = () => {
  const { t, isRTL, lang } = useLanguage();
  const { settings } = useSettings();
  useSeo({
    title: settings?.metaTitle || `${settings?.siteName || 'Maxula'} — Les meilleurs deals au Maroc`,
    description: settings?.metaDescription || 'Découvrez les meilleures offres et promotions près de chez vous sur Maxula.',
    keywords: settings?.metaKeywords
  });
  const heroBg = settings?.heroImage ? normalizeImagePath(settings.heroImage) : HERO_BG;
  // En arabe : on utilise TOUJOURS la traduction (le réglage admin est en FR).
  // En français : le réglage admin a la priorité.
  const heroTitle = isRTL ? t('home.heroTitle') : (settings?.heroTitle || t('home.heroTitle'));
  const heroSubtitle = isRTL ? t('home.heroSubtitle') : (settings?.heroSubtitle || t('home.heroSubtitle'));
  const [categories, setCategories] = useState([]);
  const [featuredDeals, setFeaturedDeals] = useState([]);
  // Date du prochain changement de lot, renvoyee par le serveur. Sert a
  // rafraichir l'accueil d'un visiteur qui laisse l'onglet ouvert : sans ca,
  // il garderait les memes offres jusqu'a ce qu'il recharge la page.
  const [rotationNextAt, setRotationNextAt] = useState(null);

  const [allDeals, setAllDeals] = useState([]);
  const [allLoading, setAllLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [total, setTotal] = useState(0);

  const [topLoading, setTopLoading] = useState(true);

  // ✅ CATEGORIES: 2x2 en mobile => 4 par vue, sinon 8
  const [catIndex, setCatIndex] = useState(0);
  const [catPerView, setCatPerView] = useState(window.innerWidth < 640 ? 4 : 8);

  // ✅ CITIES (5 par view)
  const [cityIndex, setCityIndex] = useState(0);
  const [cityArMap, setCityArMap] = useState({});
  // Liste complete des villes, avec leur photo et leur VRAI nombre d'offres.
  const [cityRows, setCityRows] = useState([]);
  // Marques : meme principe que les villes — logo, nom, nombre d'offres.
  const [brandRows, setBrandRows] = useState([]);
  const [brandIndex, setBrandIndex] = useState(0);
  const BRAND_PER_VIEW = 5;

  const CITY_PER_VIEW = 5;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ✅ update catPerView on resize
  useEffect(() => {
    const onResize = () => setCatPerView(window.innerWidth < 640 ? 4 : 8);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // TOP: categories + featured
  const loadTop = useCallback(async (signal) => {
    setTopLoading(true);
    try {
      const [catRes, featRes] = await Promise.allSettled([
        categoryAPI.getAll(),
        // v1.418.0 — ROTATION : on interroge desormais /deals/featured, qui
        // renvoie le lot du creneau horaire courant.
        //
        // AVANT : on chargeait les 80 offres les plus recentes et on gardait
        // les 3 meilleures remises. Deux consequences que Hicham a signalees :
        //   - la selection ne changeait JAMAIS (le classement par remise est
        //     stable tant qu'aucune offre n'est publiee) ;
        //   - l'etoile « mise en avant » de l'admin n'avait AUCUN effet sur
        //     l'accueil, alors que c'est exactement ce qu'elle promet.
        dealAPI.getFeatured({ limit: 3 })
      ]);

      if (catRes.status === "fulfilled") {
        const c = catRes.value?.data ?? catRes.value;
        const clist = (c?.data ?? c) || [];
        if (!signal?.aborted && mountedRef.current) {
          const arr = Array.isArray(clist) ? clist : [];
          setCategories(arr);
          setCatIndex(0);
        }
      } else {
        toast.error("Impossible de charger les catégories");
      }

      if (featRes.status === "fulfilled") {
        const d = featRes.value?.data ?? featRes.value;
        const list = (d?.data ?? d) || [];
        const normalized = normalizeDealsImages(list);

        if (!signal?.aborted && mountedRef.current) {
          // L'ordre vient du serveur : c'est lui qui detient le tour de role.
          // Le re-trier ici (par remise, par date...) casserait l'equite entre
          // marchands, puisque la meme offre repasserait toujours en tete.
          setFeaturedDeals(normalized.slice(0, 3));
          // Horodatage du prochain basculement, pour reprogrammer le minuteur.
          const nextAt = d?.rotation?.nextChangeAt || null;
          setRotationNextAt(nextAt ? new Date(nextAt).getTime() : null);
        }
      } else {
        toast.error("Impossible de charger les offres en vedette");
      }
    } catch {
      toast.error("Erreur lors du chargement de la page");
    } finally {
      if (!signal?.aborted && mountedRef.current) setTopLoading(false);
    }
  }, []);

  const loadAllDeals = useCallback(
    async (p = page, l = limit) => {
      setAllLoading(true);
      try {
        const res = await dealAPI.getAll({ sort: "newest", page: p, limit: l });
        const payload = res?.data ?? res;
        const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        const normalized = normalizeDealsImages(list);

        if (!mountedRef.current) return;

        setAllDeals(normalized);
        const t = readTotalFromResponse(res);
        setTotal(t || normalized.length || 0);

        const serverPage = payload.currentPage ?? payload.page ?? payload.data?.currentPage ?? null;
        const serverLimit = payload.itemsPerPage ?? payload.limit ?? payload.data?.itemsPerPage ?? null;
        if (serverPage) setPage(Number(serverPage));
        if (serverLimit) setLimit(Number(serverLimit));
      } catch (err) {
        console.error("Erreur chargement deals home:", err);
        toast.error("Erreur lors du chargement des offres");
        if (mountedRef.current) {
          setAllDeals([]);
          setTotal(0);
        }
      } finally {
        if (mountedRef.current) setAllLoading(false);
      }
    },
    [page, limit]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadTop(controller.signal);
    return () => controller.abort();
  }, [loadTop]);

  // ============================================================
  // v1.418.0 — BASCULE AUTOMATIQUE DU LOT MIS EN AVANT
  //
  // Le serveur indique l'heure du prochain changement de creneau. On arme un
  // minuteur unique sur cet horodatage plutot qu'un setInterval : un intervalle
  // fixe finirait par deriver par rapport aux creneaux du serveur et
  // rechargerait la page pour rien la plupart du temps.
  //
  // PIEGES TRAITES :
  //  - setTimeout est plafonne a ~24,8 jours (entier 32 bits) : au-dela le
  //    delai deborde et se declenche IMMEDIATEMENT, ce qui boucle. On borne.
  //  - Onglet en arriere-plan : les minuteurs sont brides par le navigateur.
  //    On recharge donc aussi au retour de visibilite si le creneau est passe.
  //  - Le rechargement ne touche QUE le bloc mis en avant (loadTop), pas la
  //    liste complete : recharger tout ferait sauter la position de lecture.
  // ============================================================
  useEffect(() => {
    if (!rotationNextAt) return undefined;

    let timer = null;
    const refresh = () => {
      const controller = new AbortController();
      loadTop(controller.signal);
    };

    const arm = () => {
      if (timer) clearTimeout(timer);
      const delay = rotationNextAt - Date.now();
      if (delay <= 0) { refresh(); return; }
      // +2 s de marge : declencher pile a la seconde du basculement peut
      // retomber sur le creneau precedent a cause du decalage d'horloge
      // entre le navigateur et le serveur.
      timer = setTimeout(refresh, Math.min(delay + 2000, 2147483000));
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() >= rotationNextAt) refresh();
        else arm();
      }
    };

    arm();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [rotationNextAt, loadTop]);

  useEffect(() => {
    loadAllDeals(page, limit);
  }, [loadAllDeals, page, limit]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || allDeals.length) / (limit || 1))),
    [total, allDeals.length, limit]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  // categories visibles
  const visibleCategories = useMemo(() => {
    return (categories || []).slice(catIndex, catIndex + catPerView);
  }, [categories, catIndex, catPerView]);

  const canPrevCat = catIndex > 0;
  const canNextCat = catIndex + catPerView < (categories?.length || 0);

  const prevCats = () => setCatIndex((prev) => Math.max(0, prev - catPerView));
  const nextCats = () => {
    setCatIndex((prev) => {
      const maxStart = Math.max(0, (categories?.length || 0) - catPerView);
      return Math.min(maxStart, prev + catPerView);
    });
  };

  // ========= CITIES + COUNT =========
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await cityAPI.getAll({ active: true });
        const list = r.data?.data || r.data?.cities || r.data || [];
        const m = {};
        (list || []).forEach((c) => {
          if (c && c.name) m[String(c.name).toLowerCase()] = c.nameAr || '';
        });
        if (alive) {
          setCityArMap(m);
          setCityRows(Array.isArray(list) ? list : []);
        }
      } catch (e) { /* fallback FR */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await brandAPI.getAll();
        const list = r.data?.data || r.data || [];
        if (!alive) return;
        // On ne garde que les marques qui ONT des offres : une marque a
        // « 0 offre » n'amene le visiteur sur rien, et remplit la rangee a la
        // place d'une marque utile.
        setBrandRows(
          (Array.isArray(list) ? list : []).filter((b) => Number(b.dealsCount || 0) > 0)
        );
      } catch (e) { if (alive) setBrandRows([]); }
    })();
    return () => { alive = false; };
  }, []);

  const visibleBrands = useMemo(
    () => brandRows.slice(brandIndex, brandIndex + BRAND_PER_VIEW),
    [brandRows, brandIndex]
  );
  const canPrevBrand = brandIndex > 0;
  const canNextBrand = brandIndex + BRAND_PER_VIEW < brandRows.length;
  const prevBrands = () => setBrandIndex((p) => Math.max(0, p - BRAND_PER_VIEW));
  const nextBrands = () => setBrandIndex((p) => {
    const maxStart = Math.max(0, brandRows.length - BRAND_PER_VIEW);
    return Math.min(maxStart, p + BRAND_PER_VIEW);
  });

  const citiesWithCount = useMemo(() => {
    // Le compte vient de l'API (/api/cities renvoie dealsCount), PAS des deals
    // charges sur cette page. L'ancien calcul comptait les annonces presentes
    // dans la reponse paginee de l'accueil : Tanger affichait « 8 offres »
    // parce que 8 de ses annonces figuraient dans cette page-la, pas parce
    // qu'elle en compte 8.
    if (cityRows.length) {
      return cityRows
        .map((c) => ({
          name: c.name,
          count: Number(c.dealsCount ?? c.offers ?? 0),
          image: c.image || null,
        }))
        .filter((c) => c.name)
        .sort((a, b) => {
          const dc = (b.count || 0) - (a.count || 0);
          if (dc !== 0) return dc;
          return String(a.name).localeCompare(String(b.name));
        });
    }

    // Repli si l'API des villes est indisponible : on repart des deals charges.
    // Moins juste, mais mieux qu'une section vide.
    const src = [...(featuredDeals || []), ...(allDeals || [])];
    const map = new Map();
    for (const d of src) {
      const city = pickCityFromDeal(d);
      if (!city) continue;
      const key = city.toLowerCase();
      if (!map.has(key)) map.set(key, { name: city, count: 0, image: null });
      map.get(key).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => {
      const dc = (b.count || 0) - (a.count || 0);
      if (dc !== 0) return dc;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [cityRows, allDeals, featuredDeals]);

  useEffect(() => {
    setCityIndex(0);
  }, [citiesWithCount.length]);

  const visibleCities = useMemo(() => {
    return citiesWithCount.slice(cityIndex, cityIndex + CITY_PER_VIEW);
  }, [citiesWithCount, cityIndex]);

  const canPrevCity = cityIndex > 0;
  const canNextCity = cityIndex + CITY_PER_VIEW < citiesWithCount.length;

  const prevCities = () => setCityIndex((prev) => Math.max(0, prev - CITY_PER_VIEW));
  const nextCities = () => {
    setCityIndex((prev) => {
      const maxStart = Math.max(0, citiesWithCount.length - CITY_PER_VIEW);
      return Math.min(maxStart, prev + CITY_PER_VIEW);
    });
  };

  return (
    <div className="bg-white">
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <AdBanner placement="home_top" />
      </div>
      <FlowerDecor />

      {/* MOBILE — parcours facon application : rail de categories + bannieres
          balayables, puis les offres tout de suite. La grande banniere de
          presentation coutait 330 px pour un message que personne ne lit sur
          telephone ; elle reste sur ordinateur, ou la place ne manque pas. */}
      <MobileDiscover className="md:hidden" />

      {/* ✅ HERO (DESKTOP UNIQUEMENT) */}
      <section className="relative w-full overflow-hidden hidden md:block">
        {/* DESKTOP HERO — texte d'un côté, image flottée dans le coin haut opposé.
            FR (LTR) : texte à gauche, image en haut à droite.
            AR (RTL) : miroir — texte à droite, image en haut à gauche. */}
        <div className="hidden md:block relative w-full h-[520px] overflow-hidden">
          {/* Image de fond plein écran, ancrée dans le coin HAUT du côté image.
              En AR on MIROITE l'image (scaleX -1) pour que le sujet passe du
              côté gauche, vrai miroir du FR (et non un simple recadrage vide). */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${heroBg})`,
              backgroundSize: "cover",
              backgroundPosition: "top right",
              transform: isRTL ? "scaleX(-1)" : "none"
            }}
          />
          {/* Voile dégradé : rose plein côté texte -> transparent côté image */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to ${isRTL ? "left" : "right"}, #e11d7b 0%, rgba(225,29,123,0.9) 30%, rgba(225,29,123,0.4) 58%, transparent 82%)`
            }}
          />

          {/* Panneau texte incliné, collé au bord du côté texte */}
          <div
            className="absolute top-0 h-full z-10 flex items-center text-white"
            style={{
              width: "50%",
              [isRTL ? "right" : "left"]: 0,
              backgroundColor: "#e11d7b",
              clipPath: isRTL
                ? "polygon(0 0, 100% 0, 100% 100%, 20% 100%, 5% 30%)"
                : "polygon(0 0, 100% 0, 95% 30%, 80% 100%, 0 100%)"
            }}
          >
            <div className={`max-w-xl ${isRTL ? "pe-16 ps-10" : "ps-16 pe-10"}`}>
              <h1 className="text-5xl font-extrabold leading-tight">
                {heroTitle}
              </h1>

              <p className="mt-4 text-lg text-white/90">
                {heroSubtitle}
              </p>

              <div className="mt-6">
                <Link
                  to="/deals"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white text-[#e11d7b] font-semibold shadow hover:shadow-lg transition"
                >
                  {t('home.exploreDeals')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche — 4 bannières cliquables ciblées par catégorie.
          Remplace les 3 pictogrammes Trouvez / Achetez / Profitez : même hauteur
          d'écran, mais chaque tuile est un raccourci vers des offres réelles. */}
      <QuickTiles className="hidden md:block" />

      {/* ✅ CATEGORIES */}
      <section id="categories" className="hidden md:block py-6 md:py-10 bg-[#fde7ef]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            {t('home.popularCategories')}
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            {t('home.popularCategoriesSub')}
          </p>

          {topLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">{t('home.noCategory')}</div>
          ) : (
            <div className="relative mt-6">
              {/* prev */}
              <button
                type="button"
                onClick={prevCats}
                disabled={!canPrevCat}
                className={`absolute top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40
                           ${isRTL ? "right-1 sm:right-0" : "left-1 sm:left-0"}`}
                aria-label="Précédent catégories"
              >
                {isRTL ? <FiChevronRight size={20} className="text-gray-700" /> : <FiChevronLeft size={20} className="text-gray-700" />}
              </button>

              {/* next */}
              <button
                type="button"
                onClick={nextCats}
                disabled={!canNextCat}
                className={`absolute top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40
                           ${isRTL ? "left-1 sm:left-0" : "right-1 sm:right-0"}`}
                aria-label="Suivant catégories"
              >
                {isRTL ? <FiChevronLeft size={20} className="text-gray-700" /> : <FiChevronRight size={20} className="text-gray-700" />}
              </button>

              <div className="px-10 sm:px-14">
<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-6">


                  {visibleCategories.map((cat) => (
<Link
  key={cat._id ?? cat.id}
  to={`/deals?category=${cat.slug ?? cat._id}`}
  className="
    bg-white rounded-2xl
    p-6
    min-h-[120px]
    flex flex-col items-center justify-center
    shadow-md hover:shadow-xl
    transition transform hover:-translate-y-1
    border border-pink-100
  "
  title={localizedName(cat, lang)}
>
  <div className="flex justify-center mb-3">
    {renderCategoryIcon(cat.icon, 34, "text-[#e11d7b]")}
  </div>

<div className="text-[13px] sm:text-[14px] font-bold text-gray-800 text-center leading-tight
                min-h-[38px] break-words">
  {localizedName(cat, lang)}
</div>


</Link>


                  ))}
                </div>
              </div>

              <div className="mt-4 text-center text-xs text-gray-500">
                {Math.min(catIndex + 1, categories.length)}-
                {Math.min(catIndex + catPerView, categories.length)} / {categories.length} {t('home.categoriesCount')}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Offres en vedette */}
      <section className="py-6 md:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            {t('home.featured')}
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            {t('home.featuredSub')}
          </p>

          {topLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : featuredDeals.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">{t('home.noFeatured')}</div>
          ) : (
            // Sur mobile la grille est en 1 colonne : trois cartes pleine
            // largeur, c'est plusieurs ecrans de defilement avant « Toutes les
            // offres ». On n'en montre que deux sur telephone, les trois
            // reviennent des la tablette. Le decoupage se fait en CSS
            // (`hidden` sur la 3e) et non en coupant le tableau, sinon la carte
            // masquee disparaitrait aussi au redimensionnement sans recharger.
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {featuredDeals.map((deal, i) => (
                <div key={deal._id ?? deal.id} className={i >= 2 ? 'hidden md:block' : ''}>
                  <DealCard deal={deal} showAction />
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <Link to="/deals" className="text-[#e11d7b] font-semibold hover:underline">
              {t('home.seeAllDeals')} →
            </Link>
          </div>
        </div>
      </section>

      {/* Toutes les offres */}
      <section className="py-6 md:py-10 bg-[#fff3f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            {t('home.allDeals')}
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            {t('home.allDealsSub')}
          </p>

          {allLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : allDeals.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">{t('home.noDeals')}</div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {allDeals.map((deal) => (
                  <DealCard key={deal._id ?? deal.id} deal={deal} />
                ))}
              </div>

              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || allLoading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <FiChevronLeft /> <span>{t('common.prevShort')}</span>
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                    const start = Math.max(1, Math.min(totalPages - 4, page - 2));
                    const p = start + idx;
                    if (p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToPage(p)}
                        className={`w-9 h-9 rounded-lg border text-sm font-semibold ${
                          p === page
                            ? "bg-[#e11d7b] text-white border-[#e11d7b]"
                            : "bg-white text-gray-700"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || allLoading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <span>{t('common.nextShort')}</span> <FiChevronRight />
                </button>
              </div>

              <div className="mt-3 flex justify-center">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span>{t('common.show')}</span>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="bg-white border rounded-lg px-2 py-1"
                  >
                    {[6, 9, 12, 18].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span>{t('common.perPage')}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ✅ Deals par ville */}
      <section id="cities" className="py-8 md:py-14 bg-pink-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-[#7a1147]">
            {t('home.dealsByCity')}
          </h2>
          <p className="text-center text-sm text-gray-500 mb-8">
            {t('home.dealsByCitySub')}
          </p>

          {citiesWithCount.length === 0 ? (
            <div className="text-center text-gray-500">
              {t('home.noCities')}
            </div>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={prevCities}
                disabled={!canPrevCity}
                className={`absolute top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40
                           ${isRTL ? "right-1 sm:right-0" : "left-1 sm:left-0"}`}
                aria-label="Précédent villes"
              >
                {isRTL ? <FiChevronRight size={20} className="text-gray-700" /> : <FiChevronLeft size={20} className="text-gray-700" />}
              </button>

              <button
                type="button"
                onClick={nextCities}
                disabled={!canNextCity}
                className={`absolute top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40
                           ${isRTL ? "left-1 sm:left-0" : "right-1 sm:right-0"}`}
                aria-label="Suivant villes"
              >
                {isRTL ? <FiChevronLeft size={20} className="text-gray-700" /> : <FiChevronRight size={20} className="text-gray-700" />}
              </button>

              <div className="px-10 sm:px-14">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {visibleCities.map(({ name, count, image }) => (
                    <Link
                      key={name}
                      to={`/deals?city=${encodeURIComponent(name)}`}
                      className="group relative block rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition transform hover:-translate-y-1 border border-pink-100 bg-white"
                    >
                      {/* Photo de la ville. Ratio fixe : sans lui, des images de
                          proportions differentes donneraient des cartes de
                          hauteurs inegales sur la meme ligne. */}
                      <div className="relative aspect-[4/3] bg-pink-50">
                        {image ? (
                          <>
                            <img
                              src={normalizeImagePath(image)}
                              alt={name}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover transition duration-300 group-hover:scale-105"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            {/* Voile sombre en bas : un texte blanc pose sur une
                                photo claire devient illisible. */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                          </>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <FiMapPin className="text-[#e11d7b]" size={28} />
                          </div>
                        )}

                        <div className={`absolute inset-x-0 bottom-0 p-3 text-center ${image ? 'text-white' : 'text-[#7a1147]'}`}>
                          <div className="font-semibold text-sm line-clamp-1 drop-shadow">
                            {localizedName({ name, nameAr: cityArMap[String(name).toLowerCase()] }, lang)}
                          </div>
                          <div className={`text-xs ${image ? 'text-white/85' : 'text-gray-500'}`}>
                            {count} {t('home.offers')}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-4 text-center text-xs text-gray-500">
                  {Math.min(cityIndex + 1, citiesWithCount.length)}-
                  {Math.min(cityIndex + CITY_PER_VIEW, citiesWithCount.length)} / {citiesWithCount.length} {t('home.citiesCount')}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Deals par marque — meme presentation que les villes, avec le logo a la
          place de la photo. La section disparait entierement s'il n'y a aucune
          marque avec des offres : une rangee vide donne l'impression d'un site
          en panne. */}
      {brandRows.length > 0 && (
        <section id="brands" className="py-8 md:py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-[#7a1147]">
              Deals par marque
            </h2>
            <p className="text-center text-sm text-gray-500 mb-8">
              Retrouve les offres de tes marques preferees.
            </p>

            <div className="relative">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={prevBrands}
                  disabled={!canPrevBrand}
                  aria-label="Marques precedentes"
                  className="hidden sm:flex w-10 h-10 rounded-full bg-white shadow items-center justify-center text-gray-600 disabled:opacity-30"
                >
                  ‹
                </button>

                <div className="flex-1">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {visibleBrands.map((b) => (
                      <Link
                        key={b.id || b._id || b.slug}
                        to={`/deals?brand=${encodeURIComponent(b.slug || b.name)}`}
                        className="group bg-white rounded-2xl border border-gray-200 p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition transform hover:-translate-y-1"
                      >
                        {/* Le logo est contenu (`object-contain`) et non recadre :
                            un logo tronque est pire que pas de logo du tout. */}
                        <div className="h-14 w-full flex items-center justify-center mb-2">
                          {b.logo ? (
                            <img
                              src={normalizeImagePath(b.logo)}
                              alt={b.name}
                              loading="lazy"
                              className="max-h-14 max-w-full object-contain transition duration-300 group-hover:scale-105"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="text-lg font-extrabold text-[#7a1147]">
                              {String(b.name || '?').slice(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="font-semibold text-sm text-[#7a1147] line-clamp-1">
                          {localizedName({ name: b.name, nameAr: b.nameAr }, lang)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {b.dealsCount} {t('home.offers')}
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-4 text-center text-xs text-gray-500">
                    {Math.min(brandIndex + 1, brandRows.length)}-
                    {Math.min(brandIndex + BRAND_PER_VIEW, brandRows.length)} / {brandRows.length} marques
                  </div>
                </div>

                <button
                  type="button"
                  onClick={nextBrands}
                  disabled={!canNextBrand}
                  aria-label="Marques suivantes"
                  className="hidden sm:flex w-10 h-10 rounded-full bg-white shadow items-center justify-center text-gray-600 disabled:opacity-30"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;